// Shared 404 component.
//
// Used by `_error/+Page.tsx` and by every dynamic route (`/@:handle`,
// `/s/:slug`, `/category/:id`) when the requested resource does not
// exist in the current mock set. Wraps itself in `AppShell` so callers
// can render it inline without duplicating chrome.

import type { ReactNode } from "react";
import { AppShell } from "../layout/AppShell";
import { FONT_DISPLAY, PINK, VARIANT } from "../../lib/tokens";
import { useVariant } from "../../hooks/useVariant";
import { PLATFORM_BASE_URL } from "../../lib/platform-urls";

export interface NotFoundProps {
  /** Optional headline override (default: "Diese Seite existiert nicht."). */
  headline?: ReactNode;
  /** Optional detail line under the headline. */
  detail?: ReactNode;
  /** Optional eyebrow label above the headline (default: "Nicht gefunden"). */
  eyebrow?: string;
  /**
   * Nur die Meldung, mittig im Fenster — ohne Seitenleiste, Suche und
   * Fusszeile (Jans Wunsch 18.09.2026). Fuer den Viewer: wer einem toten
   * Scrolly-Link folgt, landet sonst in der alten Discovery-Huelle, die mit
   * dem heutigen Portal nichts mehr zu tun hat. „Zur Startseite" fuehrt dann
   * zur Portal-Home statt auf „/" des Viewers.
   */
  ohneRahmen?: boolean;
}

export function NotFound({ headline, detail, eyebrow = "Nicht gefunden", ohneRahmen = false }: NotFoundProps) {
  const { variant } = useVariant();
  const v = VARIANT[variant];

  const meldung = (
      <div
        style={{
          padding: ohneRahmen ? "40px 24px" : "120px 40px",
          textAlign: "center",
          maxWidth: 640,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "inline-block",
            fontSize: 12,
            fontWeight: 600,
            color: PINK,
            letterSpacing: 1,
            textTransform: "uppercase",
            marginBottom: 10,
          }}
        >
          {eyebrow}
        </div>
        <h1
          style={{
            fontFamily: FONT_DISPLAY,
            // ohne Rahmen auch am Handy lesbar (lange Adressen brechen um)
            fontSize: ohneRahmen ? "clamp(32px, 7vw, 56px)" : 56,
            overflowWrap: "anywhere",
            fontWeight: 500,
            color: v.text,
            margin: 0,
            letterSpacing: -1.2,
            lineHeight: 1.0,
          }}
        >
          {headline ?? (
            <>
              Diese Seite <em style={{ color: PINK, fontStyle: "italic" }}>existiert nicht</em>.
            </>
          )}
        </h1>
        <p
          style={{
            color: v.muted,
            fontSize: 16,
            lineHeight: 1.5,
            marginTop: 24,
          }}
        >
          {detail ?? "Der Link ist abgelaufen oder nie da gewesen. Zurück zur Startseite:"}
        </p>
        <a
          href={ohneRahmen ? `${PLATFORM_BASE_URL}/home` : "/"}
          style={{
            display: "inline-block",
            marginTop: 24,
            background: PINK,
            color: "#fff",
            padding: "11px 24px",
            borderRadius: 999,
            fontSize: 14,
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          Zur Startseite
        </a>
      </div>
  );

  if (ohneRahmen) {
    return (
      <div
        data-testid="not-found-schlicht"
        style={{
          minHeight: "100dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: v.bg,
        }}
      >
        {meldung}
      </div>
    );
  }

  return (
    <AppShell currentNavId="" loggedIn={false}>
      {meldung}
    </AppShell>
  );
}
